import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { compose } from 'redux';

import { saveEnrolmentIdForEnrolmentManagement } from '../../../../actions/hub/hubEnrolmentManagementActions';
import { fetchEnrolments } from '../../../../actions/hub/hubEnrolmentsPageActions';
import { Enrolment } from '../../../../api/types/enrolment';
import withSelectedEnrolment from '../../../../common/hub/NewWithSelectedEnrolment';
import { CancelEntry } from '../../../../components/EnrolmentManagement/CancelEnrolment/CancelEntry';
import { FormCancelEnrolment } from '../../../../components/EnrolmentManagement/CancelEnrolment/FormCancelEnrolment';
import { EnrolmentManagementConfirmed } from '../../../../components/EnrolmentManagement/EnrolmentManagementConfirmed';
import { useGetSessionPolicy } from '../../../../hooks/queries/useSessionQuery';
import { isEnrolmentCancelRequested } from '../../../../util/helpers';
import { useSearchParams } from '../../../../util/hooks/useSearchParams';
import { PATH_HUB_CR_CANCEL_ENROLMENT } from '../../../../util/pagePath';
import { TRACKING_CE } from '../../../../util/trackingClasses';

const CANCEL_FLOW_STEPS = {
  ENTRY: 'ENTRY',
  FORM: 'FORM',
  CONFIRMED: 'CONFIRMED',
};

interface Props {
  selectedEnrolment: Enrolment;
}

export const CancelEnrolment = ({ selectedEnrolment }: Props) => {
  const { enrolmentId, enrolmentStatus, student, subject, enrolmentSfid } = selectedEnrolment;
  const dispatch = useDispatch();
  const {
    data: enrolmentPolicy,
    isLoading: pausePolicyLoading,
    error: policyError,
  } = useGetSessionPolicy({
    enrolmentId: enrolmentSfid,
  });
  const isCancelRequested = isEnrolmentCancelRequested({ enrolmentStatus });
  const query = useSearchParams();
  const goToCancelForm = query.get('view') === 'form';

  let defaultView = CANCEL_FLOW_STEPS.ENTRY;
  if (isCancelRequested) {
    defaultView = CANCEL_FLOW_STEPS.CONFIRMED;
  } else if (goToCancelForm) {
    defaultView = CANCEL_FLOW_STEPS.FORM;
  } else {
    defaultView = CANCEL_FLOW_STEPS.ENTRY;
  }

  const [activeView, setActiveView] = useState(defaultView);
  useEffect(() => {
    setActiveView(defaultView);
  }, [defaultView]);

  const cancelEntrySwitchTo = () => {
    setActiveView(CANCEL_FLOW_STEPS.FORM);
  };

  switch (activeView) {
    case CANCEL_FLOW_STEPS.CONFIRMED:
      return (
        <EnrolmentManagementConfirmed
          mainHeading="Cancellation request received"
          student={student}
          subject={subject}
          enrolmentId={enrolmentId}
          tracking={TRACKING_CE.SUCCESS}
          message="We have received your request to cancel your enrolment -
            you will receive a confirmation email with the details shortly."
          cleanUp={
            isCancelRequested
              ? () => {}
              : () => {
                  dispatch(fetchEnrolments('active'));
                  dispatch(saveEnrolmentIdForEnrolmentManagement({ enrolmentId: '' }));
                }
          }
        />
      );
    case CANCEL_FLOW_STEPS.FORM:
      return (
        <FormCancelEnrolment
          selectedEnrolment={selectedEnrolment}
          goBack={() => {
            setActiveView(CANCEL_FLOW_STEPS.ENTRY);
          }}
          submitSuccessCallback={() => {
            setActiveView(CANCEL_FLOW_STEPS.CONFIRMED);
          }}
          enrolmentPolicyError={policyError}
        />
      );
    default:
      return (
        <CancelEntry
          selectedEnrolment={selectedEnrolment}
          switchToForm={cancelEntrySwitchTo}
          pausePolicyLoading={pausePolicyLoading}
          enrolmentPolicy={enrolmentPolicy}
          enrolmentPolicyError={policyError}
        />
      );
  }
};

export default compose(withSelectedEnrolment(CancelEnrolment, null, PATH_HUB_CR_CANCEL_ENROLMENT));
